import { useEffect, useState } from "react";
import { Button, Card, Empty, Form, Input, InputNumber, Modal, Progress, Select, Space, Typography, message } from "antd";
import { Plus, Trash2 } from "lucide-react";
import axiosClient from "../../api/axiosClient";

const { Text, Title } = Typography;

interface KPI {
  kpi_id: string;
  name: string;
  metric: string;
  range_key: string;
  goal: number;
  alert_threshold: number;
  current_value: number;
}

const METRIC_LABELS: Record<string, string> = {
  total_revenue: "Total Revenue ($)",
  appointment_count: "Appointment Count",
  avg_ticket: "Average Ticket ($)",
  new_clients: "New Clients",
  retention_rate: "Retention Rate (%)",
};

const RANGE_LABELS: Record<string, string> = {
  today: "Today", week: "This Week", month: "This Month", quarter: "This Quarter", year: "This Year",
};

export default function ReportsKpiTab() {
  const [kpis, setKpis] = useState<KPI[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  function fetchKpis() {
    setLoading(true);
    axiosClient.get("/kpis").then((res) => setKpis(res.data ?? [])).catch(() => message.error("Failed to load KPIs")).finally(() => setLoading(false));
  }

  useEffect(() => { fetchKpis(); }, []);

  async function saveKpi() {
    const vals = await form.validateFields();
    try {
      await axiosClient.post("/kpis", vals);
      message.success("KPI created");
      setModalOpen(false);
      form.resetFields();
      fetchKpis();
    } catch {
      message.error("Failed to create KPI");
    }
  }

  async function removeKpi(id: string) {
    try {
      await axiosClient.delete(`/kpis/${id}`);
      fetchKpis();
    } catch {
      message.error("Failed to delete KPI");
    }
  }

  const fmtValue = (metric: string, v: number) =>
    metric === "total_revenue" || metric === "avg_ticket" ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}` :
    metric === "retention_rate" ? `${v.toFixed(1)}%` : v.toLocaleString();

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <Text type="secondary">Track custom goals computed live from your real revenue, bookings, and client data</Text>
        <Button type="primary" icon={<Plus size={14} />} onClick={() => { form.resetFields(); setModalOpen(true); }}>New KPI</Button>
      </div>

      {!loading && kpis.length === 0 && <Empty description="No custom KPIs yet" />}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
        {kpis.map((k) => {
          const pct = k.goal > 0 ? Math.min(100, (k.current_value / k.goal) * 100) : 0;
          const atRisk = pct < k.alert_threshold;
          return (
            <Card key={k.kpi_id} size="small">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <Text strong>{k.name}</Text>
                  <div style={{ fontSize: 12, color: "#8c8c8c" }}>{METRIC_LABELS[k.metric]} · {RANGE_LABELS[k.range_key]}</div>
                </div>
                <Button size="small" type="text" danger icon={<Trash2 size={13} />} onClick={() => removeKpi(k.kpi_id)} />
              </div>
              <Title level={3} style={{ margin: "12px 0 4px" }}>{fmtValue(k.metric, k.current_value)}</Title>
              <Text type="secondary" style={{ fontSize: 12 }}>Goal: {fmtValue(k.metric, k.goal)}</Text>
              <Progress percent={Math.round(pct)} status={atRisk ? "exception" : "active"} size="small" style={{ marginTop: 8 }} />
            </Card>
          );
        })}
      </div>

      <Modal title="New Custom KPI" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={saveKpi} okText="Create">
        <Form form={form} layout="vertical" style={{ marginTop: 16 }} initialValues={{ range_key: "month", alert_threshold: 80 }}>
          <Form.Item name="name" label="KPI Name" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Monthly Revenue Goal" />
          </Form.Item>
          <Form.Item name="metric" label="Metric" rules={[{ required: true, message: "Required" }]}>
            <Select options={Object.entries(METRIC_LABELS).map(([v, label]) => ({ value: v, label }))} />
          </Form.Item>
          <Form.Item name="range_key" label="Time Range">
            <Select options={Object.entries(RANGE_LABELS).map(([v, label]) => ({ value: v, label }))} />
          </Form.Item>
          <Space style={{ width: "100%" }} size={12}>
            <Form.Item name="goal" label="Goal" rules={[{ required: true, message: "Required" }]} style={{ flex: 1 }}>
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
            <Form.Item name="alert_threshold" label="Alert Below (%)" style={{ flex: 1 }}>
              <InputNumber style={{ width: "100%" }} min={0} max={100} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
}
